/**
 * Shared presentational primitives for the new conversation detail view.
 *
 * Everything here obeys the ≤3px border-radius rule (`R`) and the kc3 agent
 * brand palette. These mirror the look of CreateConversationPanel so the new
 * detail drawer feels like one system, but are self-contained (no dependency
 * on the older `modules/conversations/*` UI which uses larger radii).
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type JSX, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Check, ChevronDown, Search } from 'lucide-react'
import { useConversationStatuses } from '../../../hooks/useConversationStatuses.js'
import { presentConversationStatus, statusStyles } from '../../../modules/conversations/statusCatalog.js'
import { timestampMs } from '../../../utils/timezone.js'

/** Single source of truth for the corner radius — never exceed 3px. */
export const R = 'rounded-[3px]'

/** Roughly the dropdown's tallest rendered height — drives the flip decision. */
const MENU_MAX_HEIGHT = 280

/** Strip HTML → plain text (descriptions/messages may arrive as rich-text HTML). */
export function htmlToText(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .trim()
}

/** Shared input styling (matches CreateConversationPanel.inputClass). */
export const inputClass =
  'w-full rounded-[3px] border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 outline-none transition-all placeholder:text-slate-400 focus:border-[#DCE7F8] focus:bg-white focus:ring-2 focus:ring-[#EEF4FF]'

/** Shared multi-line input styling. */
export const textareaClass =
  'w-full resize-none rounded-[3px] border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800 outline-none transition-all placeholder:text-slate-400 focus:border-[#DCE7F8] focus:bg-white focus:ring-2 focus:ring-[#EEF4FF]'

export interface Option {
  value: string
  label: string
  disabled?: boolean
}

/* ── Labelled field wrapper ── */
export function Field({
  label,
  required,
  children,
  error,
  hint,
}: {
  label: string
  required?: boolean | undefined
  children: ReactNode
  error?: string | undefined
  hint?: string | undefined
}): JSX.Element {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold text-slate-600">
        {label}
        {required && <span className="ml-0.5 text-red-500">*</span>}
      </label>
      {children}
      {hint && !error && <p className="mt-1 text-[11px] text-slate-400">{hint}</p>}
      {error && <p className="mt-1 text-[11px] text-red-500">{error}</p>}
    </div>
  )
}

/* ── Searchable single-select dropdown (adapted from CreateConversationPanel) ── */
export function SelectField({
  icon,
  value,
  onChange,
  disabled,
  placeholder,
  error,
  options,
  loading,
  allowClear,
}: {
  icon?: JSX.Element | undefined
  value: string
  onChange: (v: string) => void
  disabled?: boolean | undefined
  placeholder: string
  error?: string | undefined
  options: Option[]
  loading?: boolean | undefined
  allowClear?: boolean | undefined
}): JSX.Element {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const menuRef = useRef<HTMLDivElement>(null)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return
    const handler = (e: MouseEvent): void => {
      const target = e.target as Node
      // The menu is portalled to <body>, so it is NOT inside `ref` — without
      // checking it too, the first click on any option closes the menu before
      // the option's own handler runs.
      if (ref.current?.contains(target)) return
      if (menuRef.current?.contains(target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [open])

  /**
   * The menu is rendered in a portal, positioned against the trigger.
   *
   * As an absolutely-positioned child it was clipped by any ancestor with
   * `overflow-hidden` or a scroll container — which is exactly what happened in
   * the advanced-filter panel when it was short: the list was cut off at the
   * panel's edge. A portal escapes every such ancestor, and the menu flips
   * above the trigger when there isn't room below.
   */
  const [menuBox, setMenuBox] = useState<{ top: number; left: number; width: number; flip: boolean } | null>(null)

  const positionMenu = useCallback(() => {
    const trigger = ref.current
    if (!trigger) return
    const rect = trigger.getBoundingClientRect()
    const below = window.innerHeight - rect.bottom
    const flip = below < MENU_MAX_HEIGHT && rect.top > below
    setMenuBox({
      top: flip ? rect.top - 4 : rect.bottom + 4,
      left: rect.left,
      width: rect.width,
      flip,
    })
  }, [])

  useLayoutEffect(() => {
    if (!open) { setMenuBox(null); return }
    positionMenu()
    // Follow the trigger while the page moves underneath it.
    window.addEventListener('scroll', positionMenu, true)
    window.addEventListener('resize', positionMenu)
    return () => {
      window.removeEventListener('scroll', positionMenu, true)
      window.removeEventListener('resize', positionMenu)
    }
  }, [open, positionMenu])

  const selected = options.find((o) => o.value === value)
  const filtered = query.trim()
    ? options.filter((o) => o.label.toLowerCase().includes(query.trim().toLowerCase()))
    : options

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        className={`${inputClass} flex items-center justify-between text-left ${icon ? 'pl-9' : ''} ${disabled ? 'cursor-not-allowed border-dashed border-slate-200 bg-slate-100 opacity-80' : 'cursor-pointer'} ${error ? 'border-red-300' : ''}`}
      >
        {icon && (
          <span className={`pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 ${disabled ? 'text-slate-300' : 'text-slate-400'}`}>
            {icon}
          </span>
        )}
        <span className={`truncate ${disabled ? 'text-slate-400' : selected ? 'text-slate-800' : 'text-slate-400'}`}>
          {loading ? 'Loading…' : selected ? selected.label : placeholder}
        </span>
        <ChevronDown className={`ml-2 h-4 w-4 shrink-0 ${disabled ? 'text-slate-300' : 'text-slate-400'}`} />
      </button>

      {open && !disabled && menuBox && createPortal(
        <div
          ref={menuRef}
          style={{
            position: 'fixed',
            top: menuBox.top,
            left: menuBox.left,
            width: menuBox.width,
            ...(menuBox.flip ? { transform: 'translateY(-100%)' } : {}),
          }}
          // Above every drawer (the tallest is z-[71]) but below the spotlight
          // guide at z-[120]. At z-[60] the menu rendered *behind* the assign
          // drawer's panel and looked like it simply never opened.
          className={`z-[90] overflow-hidden ${R} border border-slate-200 bg-white shadow-xl`}
        >
          <div className="relative border-b border-slate-100 p-2">
            <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              className={`w-full ${R} border border-slate-200 bg-slate-50 py-1.5 pl-9 pr-3 text-sm outline-none focus:border-[#DCE7F8] focus:bg-white focus:ring-2 focus:ring-[#EEF4FF]`}
            />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {allowClear && (
              <button
                type="button"
                onClick={() => {
                  onChange('')
                  setOpen(false)
                  setQuery('')
                }}
                className="flex w-full items-center px-3 py-2 text-left text-sm text-slate-400 hover:bg-slate-50"
              >
                Clear selection
              </button>
            )}
            {filtered.length === 0 && (
              <div className="px-3 py-3 text-center text-xs text-slate-400">No options</div>
            )}
            {filtered.map((o) => (
              <button
                key={o.value}
                type="button"
                disabled={o.disabled}
                onClick={() => {
                  if (o.disabled) return
                  onChange(o.value)
                  setOpen(false)
                  setQuery('')
                }}
                className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm ${
                  o.disabled
                    ? 'cursor-not-allowed text-slate-300'
                    : 'text-slate-800 hover:bg-slate-50'
                }`}
              >
                <span className="truncate">{o.label}</span>
                {value === o.value && <Check className="ml-2 h-4 w-4 shrink-0 text-[#3366FF]" />}
              </button>
            ))}
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
}

/* ── Buttons ── */
export function PrimaryButton({
  children,
  onClick,
  disabled,
  type = 'button',
  className = '',
}: {
  children: ReactNode
  onClick?: (() => void) | undefined
  disabled?: boolean | undefined
  type?: 'button' | 'submit'
  className?: string
}): JSX.Element {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center whitespace-nowrap ${R} bg-[#3366FF] px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-[#2952CC] active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    >
      {children}
    </button>
  )
}

export function GhostButton({
  children,
  onClick,
  disabled,
  className = '',
}: {
  children: ReactNode
  onClick?: (() => void) | undefined
  disabled?: boolean | undefined
  className?: string
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center whitespace-nowrap ${R} px-4 py-2 text-sm font-semibold text-slate-600 transition-colors hover:bg-slate-100 disabled:opacity-50 ${className}`}
    >
      {children}
    </button>
  )
}

export function DangerButton({
  children,
  onClick,
  disabled,
  className = '',
}: {
  children: ReactNode
  onClick?: (() => void) | undefined
  disabled?: boolean | undefined
  className?: string
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center whitespace-nowrap ${R} bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-all hover:bg-red-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 ${className}`}
    >
      {children}
    </button>
  )
}
export function ToolButton({
  label,
  icon,
  onClick,
  disabled = false,
  tone = 'ghost',
  badge,
}: {
  label: string
  icon: JSX.Element
  onClick?: (() => void) | undefined
  disabled?: boolean | undefined
  tone?: 'ghost' | 'primary' | 'warn'
  badge?: number
}): JSX.Element {
  const toneClass = disabled
    ? 'cursor-not-allowed text-slate-300'
    : tone === 'primary'
      ? 'bg-[#3366FF] text-white hover:bg-[#2952CC]'
      : tone === 'warn'
        ? 'text-red-600 hover:bg-red-50'
        : 'text-[#3366FF] hover:bg-blue-50'
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      className={`relative flex items-center gap-1.5 ${R} px-2.5 py-1.5 text-xs font-semibold transition-colors ${toneClass}`}
    >
      {icon}
      <span className="hidden lg:inline">{label}</span>
      {badge !== undefined && badge > 0 && (
        <span className={`ml-0.5 ${R} bg-amber-500 px-1.5 text-[10px] font-bold text-white`}>
          {badge}
        </span>
      )}
    </button>
  )
}

/* ── Section card (right rail + drawers) ── */
export function Section({
  title,
  action,
  children,
  className = '',
}: {
  title?: string | undefined
  action?: ReactNode
  children: ReactNode
  className?: string
}): JSX.Element {
  return (
    <div className={`${R} border border-slate-200 bg-white shadow-sm ${className}`}>
      {title && (
        <div className="flex items-center justify-between border-b border-slate-100 px-3.5 py-2.5">
          <h3 className="text-xs font-bold uppercase tracking-wide text-slate-500">{title}</h3>
          {action}
        </div>
      )}
      <div className="p-3.5">{children}</div>
    </div>
  )
}

/* ── Avatar (deterministic colour from a name/handle) ── */
export function getInitials(name: string): string {
  const trimmed = name.trim()
  if (!trimmed) return '?'
  if (trimmed.startsWith('@')) {
    const handle = trimmed.slice(1)
    const parts = handle.split(/[-_]/).filter(Boolean)
    if (parts.length >= 2) return parts.slice(0, 2).map((p) => p[0]!.toUpperCase()).join('')
    const camelParts = handle.split(/(?=[A-Z])/).filter(Boolean)
    if (camelParts.length >= 2) return camelParts.slice(0, 2).map((p) => p[0]!.toUpperCase()).join('')
    return handle.slice(0, 2).toUpperCase()
  }
  if (trimmed.includes('@') && !trimmed.includes(' ')) {
    const local = trimmed.split('@')[0] ?? trimmed
    if (local.length >= 2) return local.slice(0, 2).toUpperCase()
    const domain = trimmed.split('@')[1] ?? trimmed
    return domain.slice(0, 2).toUpperCase()
  }
  const parts = trimmed.split(/\s+/).filter(Boolean)
  return parts
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

const AVATAR_COLORS = [
  'bg-rose-100 text-rose-700',
  'bg-amber-100 text-amber-700',
  'bg-emerald-100 text-emerald-700',
  'bg-sky-100 text-sky-700',
  'bg-violet-100 text-violet-700',
  'bg-orange-100 text-orange-700',
  'bg-teal-100 text-teal-700',
  'bg-indigo-100 text-indigo-700',
]

export function getAvatarColor(name: string): string {
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length]!
}

/**
 * Gmail-style relative age of a timestamp — "35 minutes ago", "2 days ago".
 * Returns '' for anything unparseable so callers can render it unconditionally.
 */
export function formatRelative(isoString: string | null | undefined): string {
  if (!isoString) return ''
  const then = timestampMs(isoString)
  if (Number.isNaN(then)) return ''
  const secs = Math.round((Date.now() - then) / 1000)
  if (secs < 60) return 'just now'
  // [upper bound in seconds, seconds per unit, unit]
  const scale: [number, number, Intl.RelativeTimeFormatUnit][] = [
    [3600, 60, 'minute'],
    [86400, 3600, 'hour'],
    [2592000, 86400, 'day'],
    [31536000, 2592000, 'month'],
    [Infinity, 31536000, 'year'],
  ]
  const step = scale.find(([bound]) => secs < bound)!
  return new Intl.RelativeTimeFormat('en', { numeric: 'always' }).format(
    -Math.floor(secs / step[1]),
    step[2],
  )
}

export function Avatar({ name, size = 40, circular = false, colorClassName }: { name: string; size?: number; circular?: boolean; colorClassName?: string }): JSX.Element {
  return (
    <span
      className={`flex shrink-0 items-center justify-center ${circular ? 'rounded-full' : R} text-sm font-semibold ${colorClassName ?? getAvatarColor(name)}`}
      style={{ width: size, height: size }}
    >
      {getInitials(name)}
    </span>
  )
}

/* ── Conversation status pill ──
 * Label and colour come from the tenant's `conversation_status` catalog: pass
 * the conversation's linked row (id + the label/colour the API sent) and the
 * pill renders exactly what admin configured. With only the lifecycle value —
 * e.g. the client's other conversations — it still resolves through the catalog. */
export function StatusPill({
  status,
  conversationStatusId = null,
  statusName = null,
  statusColor = null,
}: {
  status: string
  conversationStatusId?: string | null
  statusName?: string | null
  statusColor?: string | null
}): JSX.Element {
  const { statuses } = useConversationStatuses()
  const present = presentConversationStatus(statuses, {
    status,
    conversationStatusId,
    statusName,
    statusColor,
  })
  const styles = statusStyles(present.color)
  return (
    <span
      className={`inline-flex items-center gap-1.5 ${R} bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700`}
      style={styles.pill}
    >
      <span
        className={`h-1.5 w-1.5 shrink-0 rounded-full ${present.color ? '' : 'bg-slate-400'}`}
        style={styles.dot}
      />
      {present.label}
    </span>
  )
}
